import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { queryKeys } from '@/lib/queryClient';
import { customersApi, mockCustomers, type CustomersParams } from '../api/customers.api';
import { APP_CONFIG } from '@/constants/config';
import type { PaginatedResponse, Customer } from '@/types';

export function useCustomers(params: CustomersParams) {
  return useQuery({
    queryKey: queryKeys.customers.list(params as Record<string, unknown>),
    queryFn: async (): Promise<PaginatedResponse<Customer>> => {
      if (APP_CONFIG.enableMockData) {
        await new Promise((resolve) => setTimeout(resolve, 500));

        let filtered = [...mockCustomers];

        // Apply filters
        if (params.search) {
          const search = params.search.toLowerCase();
          filtered = filtered.filter(
            (c) =>
              c.name.toLowerCase().includes(search) ||
              c.email?.toLowerCase().includes(search) ||
              c.phone.includes(search)
          );
        }

        if (params.status === 'active') {
          filtered = filtered.filter((c) => !c.isBlocked);
        } else if (params.status === 'blocked') {
          filtered = filtered.filter((c) => c.isBlocked);
        }

        if (params.verified !== undefined) {
          filtered = filtered.filter((c) => c.isVerified === params.verified);
        }

        // Apply pagination
        const page = params.page || 1;
        const limit = params.limit || 10;
        const start = (page - 1) * limit;
        const paginatedData = filtered.slice(start, start + limit);

        return {
          data: paginatedData,
          pagination: {
            page,
            limit,
            total: filtered.length,
            totalPages: Math.ceil(filtered.length / limit),
          },
        };
      }
      return customersApi.getAll(params);
    },
    staleTime: 30 * 1000,
  });
}

export function useCustomerStates() {
  return useQuery({
    queryKey: ['customers', 'states'],
    queryFn: () => customersApi.getStates(),
    staleTime: 60 * 1000,
  });
}

export function useCustomer(id: string) {
  return useQuery({
    queryKey: queryKeys.customers.detail(id),
    queryFn: async () => {
      if (APP_CONFIG.enableMockData) {
        await new Promise((resolve) => setTimeout(resolve, 300));
        return mockCustomers.find((c) => c.id === id) || mockCustomers[0];
      }
      return customersApi.getById(id);
    },
    enabled: !!id,
  });
}

export function useBlockCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, block }: { id: string; block: boolean }) => {
      if (APP_CONFIG.enableMockData) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return;
      }
      if (block) {
        await customersApi.block(id);
      } else {
        await customersApi.unblock(id);
      }
    },
    onSuccess: (_, { id, block }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.detail(id) });
      toast.success(block ? 'Customer blocked successfully' : 'Customer unblocked successfully');
    },
  });
}

export function useCustomerBookings(id: string) {
  return useQuery({
    queryKey: queryKeys.customers.bookings(id),
    queryFn: async () => {
      if (APP_CONFIG.enableMockData) {
        await new Promise((resolve) => setTimeout(resolve, 300));
        return [];
      }
      return customersApi.getBookings(id);
    },
    enabled: !!id,
  });
}

export function useCustomerPayments(id: string) {
  return useQuery({
    queryKey: queryKeys.customers.payments(id),
    queryFn: async () => {
      if (APP_CONFIG.enableMockData) {
        await new Promise((resolve) => setTimeout(resolve, 300));
        return [];
      }
      return customersApi.getPayments(id);
    },
    enabled: !!id,
  });
}
